/**
 * @file        src/pages/hotel/CouponList.jsx
 * @description Hotel Coupon List over `coupon_codes`, scoped to
 *              applicable_for='hotel' — a coupon created here is redeemable on a
 *              HOTEL booking only, and the list shows only hotel coupons. Paginated
 *              table plus modal add/edit; handles date fields and the `is_visible`
 *              flag that the generic lookup factory does not.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-14 Claude — Scoped to hotel (was writing into the global pool, so a
 *     "hotel" coupon discounted flights too). Fixed the Discount Type option that
 *     sent 'flat': coupon_codes' enum is ('percentage','fixed'), so every coupon
 *     saved as Flat was rejected by validation.
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) in the
 *     add/edit modal + Brand column — the API now demands `brand` on create.
 *   - 2026-08-10 Claude — Valid To can no longer be a past date, and can no
 *                          longer precede Valid From. Console-wide date rule
 *                          pass (src/lib/dateRules.js), mirrored server-side.
 *   - 2026-08-10 Claude — Added "Applies To" (All/B2C/B2B — `applicable_role`,
 *                          added to the table by migration 026 but never
 *                          exposed here) plus title/subtitle/audience props so
 *                          CorporateCouponList.jsx can reuse this page fixed to
 *                          audience=b2b instead of forking it.
 *   - 2026-09-12 Claude — Removed the "Applies To" input: the screen decides it
 *                          (this page b2c, CorporateCouponList.jsx b2b). It
 *                          defaulted to 'all', so every retail promo code was
 *                          silently redeemable by corporate buyers. The column
 *                          stays so an older 'All' row still reads as one.
 *   - 2026-08-10 Claude — Status column now shows the LIVE redemption status
 *                          (couponStatus — Active/Expired/Exhausted/Scheduled/
 *                          Inactive) instead of just the `is_active` toggle, so
 *                          a lapsed coupon reads as "Expired" here instead of
 *                          "Active" while it's already dead on the storefront.
 *   - 2026-09-15 Claude — Discount Value is now bounded on BOTH sides by the
 *                          shared rule (lib/discountRules.js). It accepted "0 or
 *                          more", so a 0% coupon saved happily — it validates on
 *                          the storefront, burns one of its uses and takes
 *                          nothing off — and a percentage coupon had no ceiling
 *                          at all, so "500%" saved and was then silently clamped
 *                          to the fare at checkout. Now > 0 always, and <= 100
 *                          when the type is Percentage.
 *   - 2026-09-15 Claude — Added the one-line note stating HOW a coupon reaches the
 *                          bill: only when the customer types the code, off the
 *                          ROOM TOTAL, stacking on top of the automatic discount.
 *                          The convenience fee is added after and is never
 *                          discounted — the opposite of the flight coupon.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-06 Claude — Domestic / International stays (migration 076), judged
 *                          on the property's country: a form field and a column.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import { useBrands } from '../../api/brands';
import { get, post, patch, del } from '../../api/client';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { inr, formatDate } from '../../lib/format';
import { todayStr, laterDate, pastDateError, dateRangeError } from '../../lib/dateRules';
import { couponStatus } from '../../lib/couponStatus';
import { discountValueError, discountValueMax } from '../../lib/discountRules';
import { HOTEL_COUPON_NOTE } from '../../lib/pricingNotes';
import { DOM_INT_SELECT_OPTIONS, domIntLabel } from '../../lib/domInt';

const ROLE_LABEL = { all: 'All (legacy)', b2c: 'B2C', b2b: 'B2B' };

function emptyForm(defaultRole) {
  return {
    brand: '', code: '', discount_type: 'percentage', discount_value: '',
    valid_from: '', valid_to: '', usage_limit: '', dom_int: '', applicable_role: defaultRole, is_active: true, is_visible: true,
  };
}

// DATE columns arrive as ISO datetime strings; date inputs need YYYY-MM-DD.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export default function CouponList({
  title = 'Hotel Coupon List',
  subtitle = 'B2C Hotel Management · shared coupon pool',
  audience = 'b2c',
} = {}) {
  const qc = useQueryClient();
  const { labelOf } = useBrands();
  // Fixed by the screen, not chosen — this page makes retail coupons and
  // CorporateCouponList.jsx makes corporate ones. Edit keeps the row's OWN role
  // (an edit here must not silently re-scope a shared 'All' coupon).
  const defaultRole = audience === 'b2b' ? 'b2b' : 'b2c';
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [modal, setModal] = useState(null); // null | { mode:'create'|'edit', row? }
  const [form, setForm] = useState(() => emptyForm(defaultRole));
  const [errors, setErrors] = useState({});

  // Hotel coupons only — the global pool also holds flight and insurance codes.
  const params = { page, pageSize, applicable_for: 'hotel', audience };
  if (applied) params.q = applied;
  const { data, isLoading } = useQuery({
    queryKey: ['coupons', 'list', params],
    queryFn: () => get('/coupons', { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const saveMut = useMutation({
    mutationFn: ({ id, body }) => (id ? patch(`/coupons/${id}`, body) : post('/coupons', body)),
    onSuccess: (_r, vars) => {
      toast.ok(vars.id ? 'Coupon updated' : 'Coupon added', 'Saved.');
      qc.invalidateQueries({ queryKey: ['coupons'] });
      setModal(null);
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/coupons/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Coupon removed.');
      qc.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  function openCreate() {
    setErrors({});
    setForm(emptyForm(defaultRole));
    setModal({ mode: 'create' });
  }

  function openEdit(row) {
    setErrors({});
    setForm({
      brand: row.brand || '',
      code: row.code || '',
      discount_type: row.discount_type || 'percentage',
      discount_value: row.discount_value ?? '',
      valid_from: toDateInput(row.valid_from),
      valid_to: toDateInput(row.valid_to),
      usage_limit: row.usage_limit ?? '',
      dom_int: row.dom_int || '',
      // Never 'all' — the wildcard is retired (admin migration 055) and the API
      // rejects it; a legacy row re-scopes to THIS screen's audience on edit.
      applicable_role: row.applicable_role === 'b2b' ? 'b2b' : defaultRole,
      is_active: !!row.is_active,
      is_visible: !!row.is_visible,
    });
    setModal({ mode: 'edit', row });
  }

  function onSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.brand) errs.brand = 'Required';
    if (!form.code.trim()) errs.code = 'Required';
    const valueErr = discountValueError(form.discount_type, form.discount_value);
    if (valueErr) errs.discount_value = valueErr;
    const validToErr = pastDateError(form.valid_to, 'Valid To') || dateRangeError(form.valid_from, form.valid_to, 'Valid From', 'Valid To');
    if (validToErr) errs.valid_to = validToErr;
    if (Object.keys(errs).length) return setErrors(errs);
    setErrors({});

    const body = {
      brand: form.brand,
      code: form.code.trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      // Created here = a hotel coupon. It will not validate on a flight.
      applicable_for: 'hotel',
      // Explicit null = both, so choosing All on edit clears a stored scope.
      dom_int: form.dom_int || null,
      applicable_role: form.applicable_role,
      is_active: !!form.is_active,
      is_visible: !!form.is_visible,
    };
    if (form.valid_from) body.valid_from = form.valid_from;
    if (form.valid_to) body.valid_to = form.valid_to;
    if (form.usage_limit !== '') body.usage_limit = Number(form.usage_limit);
    saveMut.mutate({ id: modal?.row?.id, body });
  }

  async function onDelete(row) {
    if (!(await confirm(`Delete coupon “${row.code}”?`))) return;
    removeMut.mutate(row.id);
  }

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(search);
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        actions={<button className="btn primary" onClick={openCreate}><Icon name="plus" /> Add Coupon</button>}
      />

      <div className="notice-banner info">{HOTEL_COUPON_NOTE}</div>

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Search</label>
            <input placeholder="Coupon code…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Brand</th><th>Code</th><th>Type</th><th>Value</th><th>Valid From</th><th>Valid To</th><th>Usage</th><th>Dom / Int</th><th>Applies To</th><th>Visible</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={13} className="empty">No coupons yet. Add your first one.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>{labelOf(r.brand)}</td>
                    <td className="mono">{r.code}</td>
                    <td>{r.discount_type === 'percentage' ? 'Percentage' : 'Flat'}</td>
                    <td>{r.discount_type === 'percentage' ? `${r.discount_value}%` : inr(r.discount_value)}</td>
                    <td className="muted">{formatDate(r.valid_from)}</td>
                    <td className="muted">{formatDate(r.valid_to)}</td>
                    <td>{r.used_count ?? 0}{r.usage_limit ? ` / ${r.usage_limit}` : ''}</td>
                    <td>{domIntLabel(r.dom_int)}</td>
                    <td>{ROLE_LABEL[r.applicable_role] || '—'}</td>
                    <td><Chip tone={r.is_visible ? 'ok' : 'rej'}>{r.is_visible ? 'Yes' : 'No'}</Chip></td>
                    <td><Chip tone={couponStatus(r).tone}>{couponStatus(r).label}</Chip></td>
                    <td className="row-act">
                      <button className="ib edit" aria-label="Edit" onClick={() => openEdit(r)}><Icon name="edit" /></button>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {modal ? (
        <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="lk-modal panel glass">
            <div className="lk-modal-head">
              <h3>{modal.mode === 'edit' ? 'Edit Coupon' : 'Add Coupon'}</h3>
              <button className="ib" aria-label="Close" onClick={() => setModal(null)}><Icon name="x" /></button>
            </div>
            <form className="form-grid" onSubmit={onSubmit}>
              <div className="field">
                <label>Brand <span className="req">*</span></label>
                <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} invalid={!!errors.brand} />
                {errors.brand ? <div className="field-error">{errors.brand}</div> : null}
              </div>
              <div className="field">
                <label>Code <span className="req">*</span></label>
                <input value={form.code} onChange={onChange('code')} maxLength={40} placeholder="e.g. HOTEL500" />
                {errors.code ? <div className="field-error">{errors.code}</div> : null}
              </div>
              <div className="field">
                <label>Discount Type <span className="req">*</span></label>
                {/* 'fixed', not 'flat' — 'flat' is not in the coupon_codes enum
                    and was rejected by the API on every save. */}
                <Select
                  value={form.discount_type}
                  onChange={(v) => setForm({ ...form, discount_type: v })}
                  options={[{ value: 'percentage', label: 'Percentage' }, { value: 'fixed', label: 'Flat' }]}
                />
              </div>
              <div className="field">
                <label>Discount Value <span className="req">*</span></label>
                <input type="number" min="0.01" max={discountValueMax(form.discount_type)} step="0.01" value={form.discount_value} onChange={onChange('discount_value')} placeholder={form.discount_type === 'percentage' ? 'e.g. 10 (max 100)' : 'e.g. 500'} />
                {errors.discount_value ? <div className="field-error">{errors.discount_value}</div> : null}
              </div>
              <div className="field">
                <label>Usage Limit</label>
                <input type="number" min="0" step="1" value={form.usage_limit} onChange={onChange('usage_limit')} placeholder="Unlimited if empty" />
              </div>
              <div className="field">
                <label>Domestic / International</label>
                <Select value={form.dom_int} onChange={(v) => setForm({ ...form, dom_int: v })} options={DOM_INT_SELECT_OPTIONS} />
              </div>
              <div className="field">
                <label>Valid From</label>
                <DatePicker value={form.valid_from} onChange={(v) => setForm({ ...form, valid_from: v })} />
              </div>
              <div className="field">
                <label>Valid To</label>
                <DatePicker
                  value={form.valid_to}
                  onChange={(v) => setForm({ ...form, valid_to: v })}
                  invalid={!!errors.valid_to}
                  min={laterDate(todayStr(), form.valid_from)}
                />
                {errors.valid_to ? <div className="field-error">{errors.valid_to}</div> : null}
              </div>
              <div className="field">
                <label>Status</label>
                <label className="check">
                  <input type="checkbox" checked={!!form.is_active}
                         onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  <span>Active</span>
                </label>
              </div>
              <div className="field">
                <label>Visibility</label>
                <label className="check">
                  <input type="checkbox" checked={!!form.is_visible}
                         onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} />
                  <span>Visible on website</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="btn ghost" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn primary" type="submit" disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
